"use client";

import { useMemo, useState } from "react";
import { useForm, Controller, type FieldValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z, type ZodTypeAny } from "zod";
import type {
  WContactFormContent,
  WContactFormField,
} from "@/lib/api/cms-window-content";
import {
  Field,
  Input,
  Textarea,
  Checkbox,
  FormStatus,
  Honeypot,
  SubmitButton,
} from "@/components/ui/Form";
import { Icon, type IconName } from "@/lib/icons/Icon";
import { toast } from "@/lib/notifications/toast";
import { useT } from "@/lib/i18n/context";
import { useAntiSpamToken } from "@/lib/cms/useAntiSpamToken";
import {
  wContactFormTranslations,
  type WContactFormTranslations,
} from "./WContactForm.i18n";
import styles from "./WContactForm.module.css";

/**
 * WContactForm — okno `w_contact_form` (formularz kontaktowy w strefie main).
 *
 * **Data source:** `W_CONTACT_FORM_CONTROLLER::getApiContent()` zwraca stały
 * zestaw pól (imię/nazwisko opcjonalne, email wymagany), tekst zgody RODO,
 * anti-spam token i endpoint send. Uproszczony odpowiednik `CContactForm`
 * (katalog) — BEZ mapy i pól firmowych.
 *
 * **Walidacja:** React Hook Form + Zod (jak CContactForm). Schema budowany
 * z `content.fields` + `text` (zawsze wymagane) + `personal_data` (zgoda RODO,
 * w oknie zawsze wymagana).
 *
 * **Anti-spam (4 warstwy):** HMAC token + min-delay od mountu + honeypot +
 * rate-limit per IP (backend). Send przez proxy `/api/w-contact-form/send`.
 */
export function WContactForm({ content }: WContactFormProps) {
  const t = useT(wContactFormTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <FormStatus variant="error">{t.noConfig}</FormStatus>
      </div>
    );
  }

  if (content.config.send_to === "1" && !content.config.crm_available) {
    return (
      <div className={styles.root}>
        <FormStatus variant="warning">
          {t.crmModeWithoutModule.body1}
          <strong> {t.crmModeWithoutModule.moduleName}</strong>{" "}
          {t.crmModeWithoutModule.body2}
        </FormStatus>
      </div>
    );
  }

  if (content.config.send_to !== "1" && content.config.send_to !== "2") {
    return (
      <div className={styles.root}>
        <FormStatus variant="error">
          {t.unknownSendMode(content.config.send_to)}
        </FormStatus>
      </div>
    );
  }

  return <ContactFormView content={content} t={t} />;
}

export type WContactFormProps = {
  content: WContactFormContent;
};

/* ─── Schema builder ────────────────────────────────────────────────────── */

/**
 * Buduje Zod schema z `content.fields` + `text` (wymagane) + `personal_data`
 * (zgoda RODO, wymagana gdy admin włączył `show_right_checkbox`).
 */
function buildSchema(
  fields: WContactFormField[],
  requirePersonalData: boolean,
  t: WContactFormTranslations,
) {
  const shape: Record<string, ZodTypeAny> = {};

  for (const f of fields) {
    const requiredMsg = t.validation.requiredField(f.label);
    let typeValidator: z.ZodString = z.string();
    if (f.type === "email") {
      typeValidator = typeValidator.email(t.validation.invalidEmail);
    }

    if (f.required) {
      shape[f.name] = z.string().min(1, requiredMsg).pipe(typeValidator);
    } else {
      shape[f.name] = z.union([typeValidator, z.literal("")]);
    }
  }

  shape.text = z.string().min(1, t.validation.requiredText);

  if (requirePersonalData) {
    shape.personal_data = z.literal(true, {
      message: t.validation.requiredPersonalData,
    });
  } else {
    shape.personal_data = z.boolean().optional();
  }

  return z.object(shape);
}

/* ─── Form view ────────────────────────────────────────────────────────── */

type FormValues = FieldValues;

function ContactFormView({
  content,
  t,
}: {
  content: WContactFormContent;
  t: WContactFormTranslations;
}) {
  const fields = content.fields;
  const requirePersonalData = content.config.show_right_checkbox;

  const schema = useMemo(
    () => buildSchema(fields, requirePersonalData, t),
    [fields, requirePersonalData, t],
  );

  const defaultValues = useMemo<FormValues>(() => {
    const dv: FormValues = { text: "" };
    for (const f of fields) dv[f.name] = "";
    if (requirePersonalData) dv.personal_data = false;
    return dv;
  }, [fields, requirePersonalData]);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onTouched",
  });

  const [topError, setTopError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  /* Czas mountu — min-delay liczony lokalnie (clock skew immunity). */
  const [mountedAt] = useState(() => Date.now());

  /* Fresh anti-spam token per mount - bypass Next.js SWR cache SSR'a. */
  const antispam = useAntiSpamToken([content.window_id]);

  const onSubmit = async (values: FormValues) => {
    setTopError(null);

    const ageSec = Math.floor((Date.now() - mountedAt) / 1000);
    if (ageSec < content.antispam.min_delay_seconds) {
      setTopError(
        t.status.waitBeforeSubmit(content.antispam.min_delay_seconds - ageSec),
      );
      return;
    }

    if (!antispam.token || !antispam.timestamp) {
      setTopError(antispam.error ?? t.status.sendFailed);
      return;
    }

    const fieldValues: Record<string, string> = {};
    for (const f of fields) {
      const v = values[f.name];
      fieldValues[f.name] = typeof v === "string" ? v : "";
    }

    const body: Record<string, unknown> = {
      id_window: content.window_id,
      token: antispam.token,
      timestamp: antispam.timestamp,
      [content.antispam.honeypot_field]: honeypot,
      field: fieldValues,
      text: typeof values.text === "string" ? values.text : "",
      personal_data: values.personal_data === true,
    };

    try {
      const res = await fetch(`/api/w-contact-form/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        error?: string;
        code?: number;
        errors?: Record<string, string>;
      };

      if (!res.ok || !json.ok) {
        if (
          json.errors &&
          typeof json.errors === "object" &&
          Object.keys(json.errors).length > 0
        ) {
          for (const [name, msg] of Object.entries(json.errors)) {
            if (typeof msg !== "string") continue;
            setError(name, { type: "server", message: msg });
          }
        } else {
          setTopError(
            json.message ||
              (res.status === 429 ? t.status.rateLimit : t.status.sendFailed),
          );
        }
        return;
      }

      toast.success(
        json.message || content.lang.messages?.send_ok || t.status.sendSuccess,
      );
      reset(defaultValues);
      setHoneypot("");
      /* Dodatkowy HTML (np. <script> trackingu konwersji) wstrzykiwany po
         udanej wysyłce. Robimy to w event handlerze (nie efekcie) - script
         tagi z innerHTML nie wykonują się, więc re-tworzymy je przez DOM. */
      if (content.config.extra_html) injectExtraHtml(content.config.extra_html);
    } catch (err) {
      setTopError(
        err instanceof Error
          ? t.status.networkError(err.message)
          : t.status.networkErrorGeneric,
      );
    }
  };

  return (
    <div className={styles.root}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.fieldsGrid}>
          {fields.map((f) => {
            const err = errors[f.name];
            const errMessage =
              typeof err?.message === "string" ? err.message : undefined;
            const iconName = iconFor(f.name, f.type);
            return (
              <Field
                key={f.name}
                label={f.label}
                required={f.required}
                error={errMessage}
              >
                <Input
                  type={f.type}
                  autoComplete={autocompleteFor(f.name, f.type)}
                  disabled={isSubmitting}
                  prefix={iconName ? <Icon name={iconName} /> : undefined}
                  {...register(f.name)}
                />
              </Field>
            );
          })}
        </div>

        <Field
          label={content.lang.labels?.text || t.labels.text}
          required
          error={
            typeof errors.text?.message === "string"
              ? errors.text.message
              : undefined
          }
        >
          <Textarea rows={6} disabled={isSubmitting} {...register("text")} />
        </Field>

        {requirePersonalData && (
          <div className={styles.fieldRow}>
            <Controller
              name="personal_data"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value === true}
                  onChange={(e) => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  disabled={isSubmitting}
                  aria-invalid={!!errors.personal_data || undefined}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: content.config.processing_personal_data,
                    }}
                  />
                </Checkbox>
              )}
            />
            {typeof errors.personal_data?.message === "string" && (
              <div className={styles.fieldError}>
                {errors.personal_data.message}
              </div>
            )}
          </div>
        )}

        {/* Honeypot poza RHF — boty fillują, ludzie nie. */}
        <Honeypot
          name={content.antispam.honeypot_field}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />

        {topError && <FormStatus variant="error">{topError}</FormStatus>}

        <div className={styles.actions}>
          <SubmitButton
            loading={isSubmitting || !antispam.ready}
            loadingLabel={t.sending}
            disabled={antispam.error != null}
          >
            {content.lang.send || t.send}
          </SubmitButton>
        </div>
      </form>
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

/**
 * Wstrzykuje dodatkowy HTML (z `config.extra_html`) do `<body>` po udanej
 * wysyłce. Script tagi ustawione przez `innerHTML` NIE wykonują się, więc
 * re-tworzymy każdy `<script>` jako świeży element DOM (zachowując atrybuty
 * i treść). Pozostały markup dopisujemy bez zmian.
 */
function injectExtraHtml(html: string): void {
  const holder = document.createElement("div");
  holder.innerHTML = html;
  for (const old of Array.from(holder.querySelectorAll("script"))) {
    const s = document.createElement("script");
    for (const attr of Array.from(old.attributes))
      s.setAttribute(attr.name, attr.value);
    s.textContent = old.textContent;
    document.body.appendChild(s);
  }
}

/** Mapping nazwy/typu pola -> ikona Phosphor (prefix w Input). */
function iconFor(name: string, type: string): IconName | null {
  if (type === "email") return "envelope";
  switch (name) {
    case "name":
    case "surname":
      return "user";
    default:
      return null;
  }
}

/** Mapping nazwy pola -> HTML5 `autocomplete`. */
function autocompleteFor(name: string, type: string): string {
  if (type === "email") return "email";
  switch (name) {
    case "name":
      return "given-name";
    case "surname":
      return "family-name";
    default:
      return "off";
  }
}
